export interface Customer {
  id: string;
  name: string;
  contactPerson: string;
  phone: string;
  email: string;
  city: string;
  sector: string;
  activeProjects: number;
  openQuotes: number;
  totalSales: number;
  receivable: number;
  lastContact: string;
  address: string;
  notes: string;
}

export type QuoteStatus =
  | 'Taslak'
  | 'Hazırlanıyor'
  | 'Gönderildi'
  | 'Görüşmede'
  | 'Revize'
  | 'Onaylandı'
  | 'Kaybedildi'
  | 'İptal';

export interface Quote {
  id: string;
  number: string;
  date: string;
  customerId: string;
  projectName: string;
  category: string;
  amount: number;
  estimatedCost: number;
  estimatedProfit: number;
  validityDate: string;
  status: QuoteStatus;
  lastContact: string;
  nextFollowUp: string;
  description: string;
  taxStatus: string;
  paymentPlan: string;
  deliveryTime: string;
  technicalNotes: string;
  internalNote: string;
}

export type ProjectStatus =
  | 'Devam Ediyor'
  | 'Beklemede'
  | 'Teslimi Yaklaşıyor'
  | 'Gecikti'
  | 'Tamamlandı';

export type StageState = 'Bekliyor' | 'Devam Ediyor' | 'Tamamlandı' | 'Bloke';

export interface Project {
  id: string;
  name: string;
  customerId: string;
  category: string;
  responsible: string;
  startDate: string;
  plannedDelivery: string;
  status: ProjectStatus;
  progress: number;
  salesAmount: number;
  collected: number;
  cost: number;
  currentStage: string;
  blockedStage?: string;
  description: string;
}

export type TaskStatus = 'Yapılacak' | 'Devam Ediyor' | 'Beklemede' | 'Tamamlandı';
export type TaskPriority = 'Normal' | 'Önemli' | 'Kritik';

export interface Task {
  id: string;
  title: string;
  customerId: string;
  projectId: string;
  assignee: string;
  startDate: string;
  dueDate: string;
  priority: TaskPriority;
  status: TaskStatus;
}

export interface Purchase {
  id: string;
  projectId: string;
  supplier: string;
  material: string;
  brandModel: string;
  quantity: number;
  unitPrice: number;
  total: number;
  orderDate: string;
  expectedDelivery: string;
  paymentStatus: string;
  materialStatus: string;
}

export interface Receivable {
  id: string;
  customerId: string;
  projectId: string;
  totalSales: number;
  collected: number;
  remaining: number;
  dueDate: string;
  status: string;
}

export interface Collection {
  id: string;
  date: string;
  customerId: string;
  projectId: string;
  paymentType: string;
  amount: number;
  description: string;
}

export interface Payment {
  id: string;
  date: string;
  supplier: string;
  projectId: string;
  category: string;
  amount: number;
  description: string;
}

export interface ProjectFinance {
  projectId: string;
  salesAmount: number;
  collected: number;
  remaining: number;
  materialCost: number;
  otherExpenses: number;
  totalCost: number;
  grossProfit: number;
  profitRate: number;
}

export type ServiceStatus =
  | 'Yeni Talep'
  | 'Planlandı'
  | 'Müdahale Ediliyor'
  | 'Parça Bekleniyor'
  | 'Tamamlandı';

export interface ServiceRecord {
  id: string;
  customerId: string;
  projectId: string;
  requestDate: string;
  issue: string;
  priority: TaskPriority;
  assignee: string;
  plannedDate: string;
  result: string;
  warranty: 'Ücretli' | 'Garanti';
  fee: number;
  status: ServiceStatus;
}

export interface FileItem {
  id: string;
  name: string;
  category: string;
  relatedType: 'Müşteri' | 'Proje';
  relatedId: string;
  size: string;
  uploadedDate: string;
  storagePath: string;
}

export interface AppNotification {
  id: string;
  text: string;
  type: 'warning' | 'danger' | 'info' | 'success';
  date: string;
  read: boolean;
}

export interface CalendarEvent {
  id: string;
  date: string;
  type: string;
  title: string;
  related: string;
}

export const quoteStatuses = [
  'Taslak',
  'Hazırlanıyor',
  'Gönderildi',
  'Görüşmede',
  'Revize',
  'Onaylandı',
  'Kaybedildi',
  'İptal',
] as const;

export const projectStatuses = [
  'Devam Ediyor',
  'Beklemede',
  'Teslimi Yaklaşıyor',
  'Gecikti',
  'Tamamlandı',
] as const;

export const taskStatuses = ['Yapılacak', 'Devam Ediyor', 'Beklemede', 'Tamamlandı'] as const;
export const taskPriorities = ['Normal', 'Önemli', 'Kritik'] as const;

export const purchaseMaterialStatuses = [
  'Talep Oluşturuldu',
  'Fiyat Bekleniyor',
  'Sipariş Verildi',
  'Kısmi Geldi',
  'Teslim Alındı',
  'İptal',
] as const;

export const purchasePaymentStatuses = ['Ödenmedi', 'Kısmi Ödendi', 'Ödendi'] as const;

export const serviceStatuses = [
  'Yeni Talep',
  'Planlandı',
  'Müdahale Ediliyor',
  'Parça Bekleniyor',
  'Tamamlandı',
] as const;

export const paymentTypes = ['Kapora', 'Ara ödeme', 'Bakiye', 'Diğer'] as const;

export const fileCategories = [
  'Teklif',
  'Elektrik projesi',
  'Teknik çizim',
  'PLC dosyası',
  'HMI',
  'Makine fotoğrafı',
  'Video',
  'Fatura / belge',
  'Servis belgesi',
  'Diğer',
] as const;

export const workflowStages = [
  'Proje Alındı',
  'Teknik Analiz',
  'Projelendirme',
  'Malzeme Listesi',
  'Satın Alma',
  'İmalat',
  'PLC / Yazılım',
  'Montaj',
  'Test',
  'Sahada Devreye Alma',
  'Müşteri Kontrolü',
  'Teslim',
  'Tamamlandı',
] as const;

export const projectCategories = [
  'Otomasyon Pano İmalatı',
  'MCC Sistemi',
  'RTU Sistemi',
  'PLC Otomasyonu',
  'HMI / SCADA',
  'CNC Kontrol',
  'HSC CNC Kontrol',
  'Makine İmalatı',
  'Tarım Sulama Otomasyonu',
  'Hidrofor Sistemi',
  'Dalgıç Pompa Panosu',
  'Uzaktan Kuyu Kontrolü',
  'Gıda Otomasyonu',
  'Süt Sektörü Otomasyonu',
  'Konveyör Sistemi',
  'Etiketleme / Kapak Kapatma',
  'Kurumsal Pano Revizyonu',
  'Belediye / Terfi Sistemi',
  'Diğer',
] as const;

export const sectors = ['Gıda', 'Süt / Gıda', 'Tarım', 'Kamu', 'Makine', 'Enerji', 'Tekstil'] as const;